<template>
  <div>
    <vx-card class="mb-5" title="Learning & Resources">
      <h4 class="mb-5"><u>General</u></h4>
      <p class="mb-5">
        <strong>Article:</strong> "A Graduate’s Guide to the Construction Process - Do you want to
        work for the client, contractor, subcontractor, or consultant?"
      </p>
      <p class="mb-5">
        In this article, you’ll learn about different types of employment within the construction
        industry. Will you work directly for the client, the contractor, a subcontractor, or a
        consultant? (click the button below to read it).
      </p>
      <button
        class="btn btn-secondary btn-size-medium"
        @click="
          showIframePopup(
            'https://gradaustralia.com.au/career-planning/a-graduates-guide-to-the-construction-process',
          )
        "
      >
        Who will you work for?
      </button>

      <h4 class="my-5">Video: 12 Steps of Construction</h4>
      <p class="mb-5">
        The construction process is a complex one. In this video, see it broken down into 12
        important steps and explained.
      </p>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <responsive-iframe-youtube
            :src="intro_videos[1].video"
            :posterimg="intro_videos[1].image"
          />
        </div>
      </div>

      <p class="mb-5">Now we will look at 3 example jobs:</p>
      <p class="mb-5">Trade focus – <strong>Bricklayer</strong></p>
      <p class="mb-5">Technician focus – <strong>Surveying Technician</strong></p>
      <p class="mb-5">Professional focus – <strong>Construction Manager</strong></p>

      <vs-tabs alignment="fixed" class="mb-5 no-padding overwrite-tabs">
        <vs-tab label="Bricklayer">
          <div class="py-2">
            <vx-card class="no-shadow">
              <p class="mb-5"><strong>Trade Focus: Bricklayer</strong></p>
              <p class="mb-5">
                <i><u>Article:</u> What Does a Bricklayer Do?</i>
              </p>
              <p class="mb-5">
                What exactly does a bricklayer do? Find out in this clear and concise article (click
                the image below to read it).
              </p>

              <centered-image-wrapper
                :image="
                  require('@/assets/images/experiences/construction/what_does_bricklayer_do_button.jpg')
                "
                alt="Read a graduate's guide to the construction process (opens in a new tab)"
                link="https://gradaustralia.com.au/career-planning/a-graduates-guide-to-the-construction-process"
              />

              <p class="mb-5">
                <i><u>Video:</u> Top 10 Reasons to Become a Bricklayer in Australia</i>
              </p>
              <p class="mb-5">
                There are many compelling reasons to consider becoming a bricklayer (or choose
                another construction trade) - find out the top ten reasons in this fun video.
              </p>
              <div class="vx-row my-5 flex items-center justify-center">
                <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
                  <responsive-iframe-youtube
                    :src="intro_videos[2].video"
                    :posterimg="intro_videos[2].image"
                  />
                </div>
              </div>
            </vx-card>
          </div>
        </vs-tab>
        <vs-tab label="Surveying Technician">
          <div class="py-2">
            <vx-card class="no-shadow">
              <p class="mb-5"><strong>Technician Focus: Surveying Technician</strong></p>
              <p class="mb-5">
                <i><u>Video:</u> Surveying and Mapping Technician Career Video</i>
              </p>
              <p class="mb-5">
                Not sure exactly what Surveying Technicians do? Wonder no more! This video gives a
                good explanation of the main roles and responsibilities of this job. This is a US
                video, so some of the pathways to becoming qualified may differ a little, but the
                information regarding the job itself is solid.
              </p>
              <div class="vx-row my-5 flex items-center justify-center">
                <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
                  <responsive-iframe-youtube
                    :src="intro_videos[3].video"
                    :posterimg="intro_videos[3].image"
                  />
                </div>
              </div>

              <p class="mb-5">
                <i><u>Video:</u> Day in the Life: Survey Tech</i>
              </p>
              <p class="mb-5">
                A little glimpse into a day in the life of a Survey Technician - more of a half-day
                really!
              </p>
              <div class="vx-row my-5 flex items-center justify-center">
                <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
                  <responsive-iframe-youtube
                    :src="intro_videos[4].video"
                    :posterimg="intro_videos[4].image"
                  />
                </div>
              </div>
            </vx-card>
          </div>
        </vs-tab>
        <vs-tab label="Construction Manager">
          <div class="py-2">
            <vx-card class="no-shadow">
              <p class="mb-5"><strong>Profession Focus: Construction Manager</strong></p>
              <p class="mb-5">
                <i
                  ><u>Article:</u> How to Become a Construction Manager in Australia (click the
                  button below to read it)</i
                >
              </p>
              <div class="vx-row my-5 flex items-center justify-center">
                <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
                  <button
                    type="button"
                    class="no-style img-click-button"
                    aria-label="Open the article: How to Become a Construction Manager in Australia"
                    @click="
                      showIframePopup(
                        'https://buildersacademy.com.au/how-to-become-a-construction-manager-in-australia/',
                      )
                    "
                  >
                    <img
                      src="@/assets/images/experiences/construction/construction_manager_button.jpg"
                      class="mx-auto img-responsive cursor-pointer"
                      alt=""
                    />
                  </button>
                </div>
              </div>
              <p class="mb-5">
                <i><u>Videos:</u> A Day in the Life: Construction Project</i>
              </p>

              <div class="vx-row my-5 flex items-center justify-center">
                <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
                  <responsive-iframe-youtube
                    :src="intro_videos[5].video"
                    :posterimg="intro_videos[5].image"
                  />
                </div>
              </div>
              <div class="vx-row my-5 flex items-center justify-center">
                <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
                  <responsive-iframe-youtube
                    :src="intro_videos[6].video"
                    :posterimg="intro_videos[6].image"
                  />
                </div>
              </div>
              <div class="vx-row my-5 flex items-center justify-center">
                <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
                  <responsive-iframe-youtube
                    :src="intro_videos[7].video"
                    :posterimg="intro_videos[7].image"
                  />
                </div>
              </div>
              <div class="vx-row my-5 flex items-center justify-center">
                <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
                  <responsive-iframe-youtube
                    :src="intro_videos[8].video"
                    :posterimg="intro_videos[8].image"
                  />
                </div>
              </div>

              <h4 class="mb-5">Construction Manager vs Project Manager</h4>
              <p class="mb-5">
                Although they can be similar in some of their responsibilities, project managers
                will generally have greater oversight than construction managers depending on the
                project delivery method.
              </p>
              <p class="mb-5">
                The project manager will be much more involved in the overall project, beginning
                work from pre-construction to post construction, while the construction manager will
                only be concerned with the actual building phase of the project.
              </p>
              <p class="mb-5">
                The project manager has greater responsibility to the project, as he/she oversees
                more than the construction process. Therefore, a project manager will often
                supervise a construction manager.
              </p>

              <p class="mb-5">
                Two project success stories in Australia showing the process, challenges and
                outcomes.
              </p>
              <div class="vx-row my-5 flex items-center justify-center">
                <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
                  <responsive-iframe-youtube
                    :src="intro_videos[9].video"
                    :posterimg="intro_videos[9].image"
                  />
                </div>
              </div>
              <div class="vx-row my-5 flex items-center justify-center">
                <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
                  <responsive-iframe-youtube
                    :src="intro_videos[10].video"
                    :posterimg="intro_videos[10].image"
                  />
                </div>
              </div>
            </vx-card>
          </div>
        </vs-tab>
      </vs-tabs>
    </vx-card>
  </div>
</template>

<script>
import ResponsiveIframeYoutube from '@/components/UI/ResponsiveIframeYoutube';
import store from '@/store/store';

export default {
  components: {
    ResponsiveIframeYoutube,
  },
  data() {
    return {
      intro_videos: {
        1: {
          image: require('@/assets/images/experiences/construction/construction_12_steps_splash.jpg'),
          video: 'https://www.youtube.com/embed/mbwuj58UEPg',
        },
        2: {
          image: require('@/assets/images/experiences/construction/10_reasons_to_become_a_bricklayer_splash.jpg'),
          video: 'https://www.youtube.com/embed/BSip77GfBJc',
        },
        3: {
          image: require('@/assets/images/experiences/construction/surveying_technician_1_splash.jpg'),
          video: 'https://www.youtube.com/embed/4Pv4KDgx95U',
        },
        4: {
          image: require('@/assets/images/experiences/construction/surveying_technician_1_splash.jpg'),
          video: 'https://www.youtube.com/embed/JtidP-pBN2g',
        },
        5: {
          image: require('@/assets/images/experiences/construction/construction_project_splash.jpg'),
          video: 'https://www.youtube.com/embed/NSQrSqoTjVU',
        },
        6: {
          image: require('@/assets/images/experiences/construction/construction_manager_splash.jpg'),
          video: 'https://www.youtube.com/embed/SiamJadljrw',
        },
        7: {
          image: require('@/assets/images/experiences/construction/construction_management_2_splash.jpg'),
          video: 'https://www.youtube.com/embed/7r6eWFonSaE',
        },
        8: {
          image: require('@/assets/images/experiences/construction/design_manager.jpg'),
          video: 'https://www.youtube.com/embed/rBaYqOT4vSU',
        },
        9: {
          image: require('@/assets/images/experiences/construction/brookfield_multiplex.jpg'),
          video: 'https://player.vimeo.com/video/95263350',
        },
        10: {
          image: require('@/assets/images/experiences/construction/downer_light_rail.jpg'),
          video: 'https://player.vimeo.com/video/446651928',
        },
      },
      showVideoPopupYt: false,
      ytVideoUrl: false,
    };
  },
  computed: {
    activeExperienceID() {
      return store.getters['authStudent/activeExperienceID'];
    },
    experience() {
      const loginType = localStorage.getItem('login_type');
      if (loginType == 'student') {
        const experiences = store.getters['authStudent/experiences'];
        return experiences.find((i) => i.id == this.activeExperienceID);
      } else {
        return store.getters['authTeacher/experience'];
      }
    },
  },
  provide: function () {
    return {
      closePopup: this.closePopup,
    };
  },
  methods: {
    showVideoFile(video) {
      store.dispatch('popup/showVideoPopup', video);
    },
    showVideoYt(url) {
      this.ytVideoUrl = url;
      this.showVideoPopupYt = true;
    },
    showIframePopup(url) {
      store.dispatch('popup/showIframePopup', url);
    },
  },
  beforeMount() {
    store.commit('authStudent/setActiveExperienceID', this.$route.params.id);
  },
};
</script>
